import { useState } from 'react';
import { ADJUST_REASONS, shelfLabel } from '../inventoryView';
import type { AdjustReason, InventoryScreenRow } from '../inventoryView';

/**
 * What a full count walk row can still be told beyond its count [SSD-7662]: checked by name when it has no barcode, a
 * note, a barcode correction for the catalog team, swapped for the product actually on the shelf, or moved whole to
 * another shelf of the walk. Marks need a counted row (the server writes them on a counted line); swap and move do not.
 */
export interface WalkRowActionsProps {
  row: InventoryScreenRow;
  counted: boolean;
  /** A product added on the shelf is only a row of the round once its count is saved, so it cannot be swapped before. */
  canSwap: boolean;
  checked: boolean;
  /** A product the catalog holds a barcode for is checked by scanning it; only one with none is checked by name. */
  hasBarcode: boolean;
  note: string;
  correction: string;
  /** The walk's other shelves, in walk order; a move goes to one of them. */
  otherShelves: string[];
  busy: boolean;
  onCheckByName: (reason: string) => void;
  onSaveNote: (text: string) => void;
  onSaveCorrection: (code: string) => void;
  onSwap: (foundSku: string, foundQty: number, reason: AdjustReason) => void;
  onMove: (toShelf: string) => void;
  onClose: () => void;
}

export const WalkRowActions = ({
  row,
  counted,
  canSwap,
  checked,
  hasBarcode,
  note,
  correction,
  otherShelves,
  busy,
  onCheckByName,
  onSaveNote,
  onSaveCorrection,
  onSwap,
  onMove,
  onClose,
}: WalkRowActionsProps) => {
  const [nameReason, setNameReason] = useState('');
  const [noteText, setNoteText] = useState(note);
  const [correctionCode, setCorrectionCode] = useState(correction);
  const [foundSku, setFoundSku] = useState('');
  const [foundQty, setFoundQty] = useState('');
  const [swapReason, setSwapReason] = useState<AdjustReason | ''>('');
  const [toShelf, setToShelf] = useState('');
  const qty = Number(foundQty);
  const swapReady =
    foundSku.trim() !== '' && foundSku.trim() !== row.sku && Number.isInteger(qty) && qty >= 1 && swapReason !== '';

  return (
    <div className="fc-more" role="group" aria-label={`More for ${row.sku}`}>
      {counted ? (
        <>
          {!checked && !hasBarcode && (
            <div className="fc-more-line">
              <label>
                No barcode · checked by name
                <input
                  className="inp"
                  aria-label={`Name check reason ${row.sku}`}
                  placeholder="Why no scan (e.g. no barcode on the box)"
                  maxLength={200}
                  value={nameReason}
                  disabled={busy}
                  onChange={(event) => setNameReason(event.target.value)}
                />
              </label>
              <button
                type="button"
                className="btn btn-line btn-sm"
                disabled={busy || nameReason.trim() === ''}
                onClick={() => onCheckByName(nameReason.trim())}
              >
                Confirm by name
              </button>
            </div>
          )}
          <div className="fc-more-line">
            <label>
              Note
              <input
                className="inp"
                aria-label={`Note ${row.sku}`}
                maxLength={200}
                value={noteText}
                disabled={busy}
                onChange={(event) => setNoteText(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn-line btn-sm"
              disabled={busy || noteText.trim() === note.trim()}
              onClick={() => onSaveNote(noteText.trim())}
            >
              Save note
            </button>
          </div>
          <div className="fc-more-line">
            <label>
              Barcode should be
              <input
                className="inp"
                aria-label={`Barcode correction ${row.sku}`}
                placeholder="Suggestion only, the product master is not changed"
                maxLength={64}
                value={correctionCode}
                disabled={busy}
                onChange={(event) => setCorrectionCode(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn-line btn-sm"
              disabled={busy || correctionCode.trim() === correction.trim()}
              onClick={() => onSaveCorrection(correctionCode.trim())}
            >
              Save correction
            </button>
          </div>
        </>
      ) : (
        <div className="fc-more-line fc-more-hint">
          Count the row first to add a name check, a note or a correction.
        </div>
      )}
      {canSwap && (
        <div className="fc-more-line">
          <label>
            Swap for SKU
            <input
              className="inp"
              aria-label={`Swap SKU ${row.sku}`}
              placeholder="SKU actually on the shelf"
              maxLength={64}
              value={foundSku}
              disabled={busy}
              onChange={(event) => setFoundSku(event.target.value)}
            />
          </label>
          <input
            className="inp fc-more-qty"
            type="number"
            min={1}
            aria-label={`Swap quantity ${row.sku}`}
            placeholder="Qty"
            value={foundQty}
            disabled={busy}
            onChange={(event) => setFoundQty(event.target.value)}
          />
          <select
            className="sel"
            aria-label={`Swap reason ${row.sku}`}
            value={swapReason}
            disabled={busy}
            onChange={(event) => setSwapReason(event.target.value as AdjustReason | '')}
          >
            <option value="">Reason…</option>
            {ADJUST_REASONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-line btn-sm"
            disabled={busy || !swapReady}
            onClick={() => swapReason && onSwap(foundSku.trim(), qty, swapReason)}
          >
            Swap
          </button>
        </div>
      )}
      {otherShelves.length > 0 && row.onHand > 0 && (
        <div className="fc-more-line">
          <label>
            Move all {row.onHand} to
            <select
              className="sel"
              aria-label={`Move to shelf ${row.sku}`}
              value={toShelf}
              disabled={busy}
              onChange={(event) => setToShelf(event.target.value)}
            >
              <option value="">Shelf…</option>
              {otherShelves.map((shelf) => (
                <option key={shelf} value={shelf}>
                  {shelfLabel(shelf)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn-line btn-sm"
            disabled={busy || toShelf === ''}
            onClick={() => onMove(toShelf)}
          >
            Move
          </button>
        </div>
      )}
      <button type="button" className="btn btn-line btn-sm fc-more-close" onClick={onClose}>
        Close
      </button>
    </div>
  );
};
